import type { ReactNode } from 'react';

export type IconName =
  | 'profile'
  | 'candidates'
  | 'match'
  | 'assistant'
  | 'team'
  | 'advisor'
  | 'hall'
  | 'tasks'
  | 'events'
  | 'settings'
  | 'admin'
  | 'news'
  | 'messages'
  | 'find';

/** 导航图标（stroke 风格，viewBox 24×24），侧边栏与移动端底部标签栏共用。 */
export const ICON_PATHS: Record<IconName, ReactNode> = {
  profile: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M4.8 20c1.2-3.4 4-5.1 7.2-5.1s6 1.7 7.2 5.1" />
    </>
  ),
  candidates: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19.5c1-2.9 3.2-4.4 5.5-4.4s4.5 1.5 5.5 4.4" />
      <path d="M17 8v6M14 11h6" />
    </>
  ),
  match: <path d="M4 18V9M10 18V5M16 18v-6M21 19H3" />,
  assistant: (
    <>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H9l-4.2 3.4a.5.5 0 0 1-.8-.4V6.5Z" />
      <path d="M8.5 9.5h.01M12 9.5h.01M15.5 9.5h.01" strokeWidth="2.4" />
    </>
  ),
  team: (
    <>
      <circle cx="12" cy="7.5" r="2.8" />
      <circle cx="5.5" cy="11" r="2.2" />
      <circle cx="18.5" cy="11" r="2.2" />
      <path d="M7 19c.9-3 2.8-4.6 5-4.6s4.1 1.6 5 4.6" />
    </>
  ),
  /** 指导老师：学士帽（导师/学术指导意象） */
  advisor: (
    <>
      <path d="M12 4 3 7.8 12 11.6l9-3.8L12 4Z" />
      <path d="M6.2 9.8v3.6c0 1.6 2.6 2.9 5.8 2.9s5.8-1.3 5.8-2.9V9.8" />
      <path d="M21 8.2v4" />
    </>
  ),
  /** 组队大厅：门/厅意象（门框 + 门内光点） */
  hall: (
    <>
      <path d="M4.5 20.5V5.2c0-.9.7-1.7 1.7-1.7h11.6c1 0 1.7.8 1.7 1.7v15.3" />
      <path d="M9.5 20.5v-5.5h5v5.5" />
      <circle cx="12" cy="9" r="1.6" />
    </>
  ),
  tasks: (
    <>
      <rect x="3.5" y="4.5" width="17" height="16" rx="2.5" />
      <path d="M8 3v3M16 3v3M7.5 12l2 2 3.5-3.5M14.5 16.5h3" />
    </>
  ),
  events: (
    <>
      <path d="M8 4h8M8 4c-1.8 0-3 1.2-3 3v11c0 1.8 1.2 3 3 3h8c1.8 0 3-1.2 3-3V7c0-1.8-1.2-3-3-3" />
      <path d="M8.5 13.5h2.5l1-2 1.5 4 1-2h2.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.4M12 18.6V21M4.2 7.5l2 1.2M17.8 15.3l2 1.2M4.2 16.5l2-1.2M17.8 8.7l2-1.2" />
    </>
  ),
  /** 管理后台：盾牌 + 对勾（平台管理员专属入口） */
  admin: (
    <>
      <path d="M12 3.5 5 6v5.2c0 4.3 2.9 7.6 7 9.3 4.1-1.7 7-5 7-9.3V6l-7-2.5Z" />
      <path d="m9.2 11.8 2 2 3.6-3.8" />
    </>
  ),
  /** 竞赛资讯：文档 + 火花（AI 智能读取意象） */
  news: (
    <>
      <path d="M6 3.5h8.5L19 8v10.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 18.5v-14A1.5 1.5 0 0 1 6 3.5Z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M9 13.5l1.3 2.4 2.4 1.3-2.4 1.3L9 21l-1.3-2.5L5.3 17l2.4-1.3L9 13.5Z" />
    </>
  ),
  /** 消息：两个叠放的对话气泡（群聊+私聊） */
  messages: (
    <>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h7A2.5 2.5 0 0 1 16 6.5v4a2.5 2.5 0 0 1-2.5 2.5H8l-3.2 2.6a.5.5 0 0 1-.8-.4V6.5Z" />
      <path d="M18 9.5h.5A2.5 2.5 0 0 1 21 12v5.6a.5.5 0 0 1-.8.4L18 15.5h-3a2.5 2.5 0 0 1-2-1" />
    </>
  ),
  /** 找队友：放大镜 + 人形（统一的找队友入口） */
  find: (
    <>
      <circle cx="10.5" cy="8" r="3" />
      <path d="M5 19c.9-3 3-4.6 5.5-4.6" />
      <circle cx="16.5" cy="15.5" r="3.2" />
      <path d="m19 18 2.2 2.2" />
    </>
  ),
};

export function NavIcon({ name, className = 'nav-ico' }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
